import React, {useMemo, useState} from 'react';
import {classes, style} from './doppe-viewer-lock-wizard-modal.st.css';
import {assertDefined, ObservableValue, uiPartCreateMarking, uiPartDefineGroups} from '@wix/devzai-utils-common';
import {DoppeDtoAction} from '../../client-server-common/types/doppe-dto-action';
import {
    BymoPageLayout,
    BymoPageStyleScope,
    BymoPageStyleScopeContext,
    useBymoPageLayout
} from '../bymo-page-style/bymo-page-style';
import {DoppeViewerModalPanel, DoppeViewerModalPanelLayout} from '../doppe-viewer-modal-panel/doppe-viewer-modal-panel';
import {useDoppeViewerClientServices} from '../../client/doppe-user-app/doppe-viewer-client-services';
import {FlexLayout} from '@wix/devzai-common-client';
import {WidgetRenderingContextType} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {DoppeViewerActionPopup} from '../doppe-viewer-action-popup/doppe-viewer-action-popup';
import {flexColumn, useObservableValue} from '@wix/devzai-utils-react';
import {DoppeActionLockPreview, DoppeActionLockReason} from '../doppe-action-lock-preview/doppe-action-lock-preview';
import {DoppeViewerModalPanelHeader} from '../doppe-viewer-modal-panel-header/doppe-viewer-modal-panel-header';
import {doppeHideableValueGetVisibleValue, useBymoPageProps} from '../../client-server-common';
import {useDoppeViewerLocales} from '../../client/doppe-viewer-locales/doppe-viewer-locales';
import {DoppeActionLockPreviewMode} from '../../client-server-common/types/doppe-action-lock-preview-mode';
import {DoppeViewerLockWizardModalParts} from "./doppe-viewer-lock-wizard-modal.ui-parts";
import {
    DoppeViewerWidgetRenderingScope
} from "../doppe-viewer-widget-rendering-context/doppe-viewer-widget-rendering-context";

export const DoppeViewerActionViewUiPartsGroups = uiPartDefineGroups({
    ActionFrameElement: 'doppe-viewer-action-view-action-frame'
});

export const DoppeViewerLockWizardModal = React.memo(function DoppeViewerLockWizardModal(props: DoppeViewerLockWizardModal.Props) {
    const {
        showUnlockPreview,
        lockedActionId,
        mobileLayout = DoppeViewerModalPanelLayout.FullScreen,
        desktopLayout = DoppeViewerModalPanelLayout.Inner,
        unlockedCallback,
        closeCallback,
        ...actionViewerProps
    } = props;

    const clientServices = useDoppeViewerClientServices();
    const bymoPageProps = useBymoPageProps();

    const lockedAction = useMemo(() => {
        return assertDefined(bymoPageProps.actions.find(action => action.id === lockedActionId));
    }, [bymoPageProps.actions, lockedActionId]);

    const {
        customLockPreviewDescription,
        customLockPreviewMedia,
        customLockPreviewTitle,
        lockPreviewShowActionHeader,
        customLockPreviewButtonText,
        lockActions,
        lockPreviewMode,
        customPreviewContent
    } = lockedAction.lockSettings

    const isDefaultLockPreviewMode = !customPreviewContent;
    const isCustomLockPreviewMode = lockPreviewMode === DoppeActionLockPreviewMode.Custom;

    const [isLockPreviewVisible, setIsLockPreviewVisible] = useState(showUnlockPreview);

    const currentActionLock = useMemo(() => {
        return lockActions[0];
    }, [lockActions])

    const layout = useBymoPageLayout();
    const isMobile = layout === BymoPageLayout.Narrow;
    const isFullScreen = (DoppeViewerModalPanelLayout.FullScreen === mobileLayout && isMobile) || (DoppeViewerModalPanelLayout.FullScreen === desktopLayout && !isMobile)

    const actionType = assertDefined(
        clientServices.actionTypesStore.getRegisteredActionType(currentActionLock.actionTypeId),
        `Action type with id '${currentActionLock.actionTypeId}' isn't registered`);


    const observableCurrentItemIdToRender = useMemo(() => {
        return actionType.resolveListItems ? new ObservableValue<string | null>(null) : undefined;
    }, [actionType]);

    const renderingInnerStateController = useMemo(() => {
        return new ObservableValue(false);
    }, []);

    const canGoBack = () => {
        return renderingInnerStateController.getValue();
    };

    const goBack = () => {
        renderingInnerStateController.setValue(false);
        observableCurrentItemIdToRender?.setValue(null);
    };

    const isInnerWidgetRendered = useObservableValue(renderingInnerStateController);

    const viewerLocales = useDoppeViewerLocales();

    const {
        name
    } = lockedAction;

    const unlockedMetaData = useMemo(() => {
        return clientServices.lockService.getActionUnlockStateFromCookie(lockedActionId);
    }, [lockedActionId]);

    return (
        <BymoPageStyleScopeContext.Provider
            value={BymoPageStyleScope.Popup}
        >
            <DoppeViewerModalPanel
                className={style(classes.root, {}, flexColumn())}
                mobileLayout={mobileLayout}
                desktopLayout={desktopLayout}
                {...uiPartCreateMarking(DoppeViewerLockWizardModalParts.RootElement)}
            >
                <DoppeViewerWidgetRenderingScope
                    widgetRenderingContextType={WidgetRenderingContextType.Modal}
                    renderingInnerStateController={renderingInnerStateController}
                    observableCurrentItemIdToRender={observableCurrentItemIdToRender}
                    isFullScreen={isFullScreen}
                    lockController={{
                        unlock: (unlockedMetaData) => {
                            return unlockedCallback(unlockedMetaData);
                        },
                        lockedActionId: lockedAction.id,
                    }}
                    headerCustomSettings={{
                        backButtonCallback: isInnerWidgetRendered || isFullScreen ? () => {
                            canGoBack() ? goBack() : closeCallback?.()
                        } : undefined,
                        dismissButtonCallback: !isFullScreen ? closeCallback : undefined,
                        title: currentActionLock.name,
                    }}
                    closeCallback={closeCallback}
                >
                    {
                        isLockPreviewVisible && isCustomLockPreviewMode ?
                            <div
                                className={style(classes.lockPreviewContent, {}, flexColumn())}
                            >
                                <DoppeViewerModalPanelHeader
                                    className={style(classes.lockPreviewContentHeader, {})}
                                    dismissButtonCallback={closeCallback}
                                    title={lockPreviewShowActionHeader ? (name ?? viewerLocales.lockTitle) : ''}
                                />
                                <DoppeActionLockPreview
                                    useDefaultContent={isDefaultLockPreviewMode}
                                    heading={customLockPreviewTitle}
                                    description={customLockPreviewDescription}
                                    media={customLockPreviewMedia}
                                    buttonText={doppeHideableValueGetVisibleValue(customLockPreviewButtonText, '')}
                                    unlockCallback={async () => {
                                        if (unlockedMetaData) {
                                            const isUnlocked = await unlockedCallback(unlockedMetaData, true);

                                            if (isUnlocked) {
                                                return;
                                            }
                                            else {
                                                setIsLockPreviewVisible(false);
                                            }
                                        }
                                        else {
                                            setIsLockPreviewVisible(false);
                                        }
                                    }}
                                    reason={DoppeActionLockReason.Lock}
                                />
                            </div>
                            :
                            <DoppeViewerActionPopup
                                className={style(classes.actionPopup, {}, FlexLayout.fillRemainingVerticalSpace)}
                                {...actionViewerProps}
                                action={currentActionLock}
                                ignoreActionViewedEvent={false}
                            />
                    }
                </DoppeViewerWidgetRenderingScope>
            </DoppeViewerModalPanel>
        </BymoPageStyleScopeContext.Provider>
    )
});

export namespace DoppeViewerLockWizardModal {

    export interface Props extends React.HTMLAttributes<any> {
        lockedActionId: DoppeDtoAction.Id;
        mobileLayout?: DoppeViewerModalPanelLayout;
        desktopLayout?: DoppeViewerModalPanelLayout;
        unlockedCallback: (unlockedMetadata?: any, isUnlockedFromCookie?: boolean) => Promise<boolean>;
        closeCallback: () => void;
        showUnlockPreview: boolean;
    }
}
